import { useCallback, useMemo } from 'react';
import { useExcelExport } from './useExcelExport';

export interface ExportSurveyItemsArgs {
    surveyId: string;
    includeCostPricesAndMargins?: boolean;
}

interface UseExportSurveyItemsResult {
    exporting: boolean;
    exportError: Error | undefined;
    exportSurveyItems: (
        surveyId: string,
        includeCostPricesAndMargins?: boolean,
    ) => Promise<void>;
}

export const useExportSurveyItems = (): UseExportSurveyItemsResult => {
    const config = useMemo(
        () => ({
            notificationId: 'survey-items',
            loadingTitle: 'Exporting Survey Items',
            loadingMessage: 'Please wait while we export the survey items',
            successTitle: 'Export Successful',
            successMessage: 'Your Excel file has been downloaded.',
            failedTitle: 'Export Failed' as const,
            filenamePrefix: 'survey_items',
            exportPath: '/export/survey-items',
            buildBody: ({
                surveyId,
                includeCostPricesAndMargins = true,
            }: ExportSurveyItemsArgs) => ({
                surveyId,
                includeCostPricesAndMargins,
            }),
        }),
        [],
    );

    const { exporting, exportError, runExport } =
        useExcelExport<ExportSurveyItemsArgs>(config);

    const exportSurveyItems = useCallback(
        async (surveyId: string, includeCostPricesAndMargins = true) => {
            await runExport({ surveyId, includeCostPricesAndMargins });
        },
        [runExport],
    );

    return {
        exporting,
        exportError,
        exportSurveyItems,
    };
};
